/**
 * Multi-account storage service for TurnKey Coach
 *
 * Manages multiple user accounts in localStorage, enabling users to:
 * - Stay logged into multiple accounts simultaneously
 * - Switch between accounts without re-authentication
 * - Log out of individual accounts
 *
 * Storage is device-specific (localStorage), not synced across devices.
 */

import Honeybadger from "@honeybadger-io/js";
import { clearNavigationReferrer } from "./index";

const STORAGE_KEY = "tkc_accounts";
const STORAGE_VERSION = 1;

/**
 * Get the stored accounts data from localStorage
 * @returns {{ version: number, accounts: Array, activeAccountIndex: number } | null}
 */
export const getStorageData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data);

    // Validate storage version
    if (parsed.version !== STORAGE_VERSION) {
      // Clear incompatible storage
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (e) {
    Honeybadger.notify(e);
    return null;
  }
};

/**
 * Save accounts data to localStorage
 * @param {Object} data - The storage data to save
 */
const setStorageData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    Honeybadger.notify(e);
  }
};

/**
 * Get all stored accounts
 * @returns {Array} Array of account objects
 */
export const getAccounts = () => {
  const data = getStorageData();
  return data?.accounts || [];
};

/**
 * Get the currently active account
 * @returns {Object | null} The active account or null if none
 */
export const getActiveAccount = () => {
  const data = getStorageData();
  if (!data || !data.accounts.length) return null;

  const index = data.activeAccountIndex || 0;
  return data.accounts[index] || data.accounts[0] || null;
};

/**
 * Get the active account index
 * @returns {number} The index of the active account
 */
export const getActiveAccountIndex = () => {
  const data = getStorageData();
  return data?.activeAccountIndex || 0;
};

/**
 * Check if there are multiple accounts stored
 * @returns {boolean} True if more than one account exists
 */
export const hasMultipleAccounts = () => {
  const accounts = getAccounts();
  return accounts.length > 1;
};

/**
 * Find an account by user ID
 * @param {number} userId - The user ID to find
 * @returns {Object | null} The account or null if not found
 */
export const findAccountByUserId = (userId) => {
  const accounts = getAccounts();
  return accounts.find((acc) => acc.userId === userId) || null;
};

/**
 * Find an account's index by user ID
 * @param {number} userId - The user ID to find
 * @returns {number} The index or -1 if not found
 */
export const findAccountIndexByUserId = (userId) => {
  const accounts = getAccounts();
  return accounts.findIndex((acc) => acc.userId === userId);
};

/**
 * Add a new account or update existing one
 * @param {Object} accountData - Account data to add/update
 * @param {number} accountData.userId - User ID
 * @param {string} accountData.email - User email
 * @param {string} accountData.fullName - User full name
 * @param {string} [accountData.avatarUrl] - User avatar URL
 * @param {number} accountData.companyId - Company ID
 * @param {string} accountData.companyName - Company name
 * @param {string} [accountData.companyLogoUrl] - Company logo URL
 * @param {string} accountData.accessToken - Bearer token
 * @param {string} [accountData.refreshToken] - Refresh token
 * @param {string} accountData.expiresAt - Token expiration ISO timestamp
 * @returns {number} The index of the added/updated account
 */
export const addAccount = (accountData) => {
  const data = getStorageData() || {
    version: STORAGE_VERSION,
    accounts: [],
    activeAccountIndex: 0,
  };

  // Check if account already exists
  const existingIndex = data.accounts.findIndex(
    (acc) => acc.userId === accountData.userId,
  );

  if (existingIndex >= 0) {
    // Update existing account
    data.accounts[existingIndex] = {
      ...data.accounts[existingIndex],
      ...accountData,
      updatedAt: new Date().toISOString(),
    };
    setStorageData(data);
    return existingIndex;
  }

  // Add new account
  const newAccount = {
    ...accountData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  data.accounts.push(newAccount);
  setStorageData(data);

  return data.accounts.length - 1;
};

/**
 * Remove an account by user ID
 * @param {number} userId - User ID to remove
 * @returns {boolean} True if account was removed
 */
export const removeAccount = (userId) => {
  const data = getStorageData();
  if (!data) return false;

  const index = data.accounts.findIndex((acc) => acc.userId === userId);
  if (index < 0) return false;

  // Remove the account
  data.accounts.splice(index, 1);

  // Adjust active index if needed
  if (data.accounts.length === 0) {
    data.activeAccountIndex = 0;
  } else if (data.activeAccountIndex >= data.accounts.length) {
    data.activeAccountIndex = data.accounts.length - 1;
  } else if (data.activeAccountIndex > index) {
    data.activeAccountIndex--;
  }

  setStorageData(data);
  return true;
};

/**
 * Set the active account by user ID and switch the server session
 * @param {number} userId - User ID to make active
 * @returns {boolean} True if account was found and switch initiated
 */
export const setActiveAccount = async (userId) => {
  const data = getStorageData();
  if (!data) return false;

  const index = data.accounts.findIndex((acc) => acc.userId === userId);
  if (index < 0) return false;

  // Update localStorage first
  data.activeAccountIndex = index;
  setStorageData(data);

  // Clear navigation referrer before switching accounts
  clearNavigationReferrer();

  try {
    // Call the backend to switch the server session
    const response = await fetch("/api/v1/account_switcher/switch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
      body: JSON.stringify({ user_id: userId }),
    });

    if (response.ok) {
      // Always redirect to home page after switching accounts
      // This ensures a clean state and consistent UX across account switches
      window.location.href = "/";
    } else if (response.status === 403) {
      // Session expired (e.g., after browser/system restart).
      // The server no longer recognizes this account as authenticated.
      // Remove the stale account and redirect to re-login.
      removeAccount(userId);
      window.location.href =
        "/users/sign_in?multi_account=true&session_expired=true";
    } else {
      Honeybadger.notify(
        new Error(`Failed to switch account: ${response.status}`),
      );
      // Revert localStorage change on failure
      data.activeAccountIndex = data.accounts.findIndex(
        (acc) => acc.userId !== userId,
      );
      setStorageData(data);
      return false;
    }
  } catch (error) {
    Honeybadger.notify(error);
    return false;
  }

  return true;
};

/**
 * Set the active account index without reloading
 * @param {number} index - Index to set as active
 * @returns {boolean} True if index was valid and set
 */
export const setActiveAccountIndex = (index) => {
  const data = getStorageData();
  if (!data) return false;

  if (index < 0 || index >= data.accounts.length) return false;

  data.activeAccountIndex = index;
  setStorageData(data);
  return true;
};

/**
 * Check if a token is expired or expiring soon (within 5 minutes)
 * @param {Object} account - Account object with expiresAt
 * @returns {boolean} True if token is expired or expiring soon
 */
export const isTokenExpired = (account) => {
  if (!account?.expiresAt) return true;

  const expiresAt = new Date(account.expiresAt);
  const now = new Date();
  const fiveMinutes = 5 * 60 * 1000;

  return expiresAt.getTime() - now.getTime() < fiveMinutes;
};

/**
 * Update an account's token
 * @param {number} userId - User ID to update
 * @param {string} accessToken - New access token
 * @param {string} [refreshToken] - New refresh token
 * @param {string} expiresAt - New expiration timestamp
 * @returns {boolean} True if token was updated
 */
export const updateAccountToken = (
  userId,
  accessToken,
  refreshToken,
  expiresAt,
) => {
  const data = getStorageData();
  if (!data) return false;

  const index = data.accounts.findIndex((acc) => acc.userId === userId);
  if (index < 0) return false;

  data.accounts[index].accessToken = accessToken;
  if (refreshToken) {
    data.accounts[index].refreshToken = refreshToken;
  }
  data.accounts[index].expiresAt = expiresAt;
  data.accounts[index].updatedAt = new Date().toISOString();

  setStorageData(data);
  return true;
};

/**
 * Clear all accounts from storage
 */
export const clearAllAccounts = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Initialize account storage with current session data
 * Call this on initial page load to capture the current session
 * @param {Object} sessionData - Current session account data
 */
export const initializeFromSession = (sessionData) => {
  if (!sessionData?.userId) return;

  const existingAccount = findAccountByUserId(sessionData.userId);

  // If account already exists, update its data and make sure it's active
  if (existingAccount) {
    const data = getStorageData();
    const index = findAccountIndexByUserId(sessionData.userId);

    // Update account data (e.g., logo URL may have changed)
    data.accounts[index] = {
      ...data.accounts[index],
      ...sessionData,
      updatedAt: new Date().toISOString(),
    };

    // Ensure this account is active
    data.activeAccountIndex = index;
    setStorageData(data);
    return;
  }

  // Add the current session as a new account
  addAccount(sessionData);

  // Set as active
  const data = getStorageData();
  const index = findAccountIndexByUserId(sessionData.userId);
  data.activeAccountIndex = index;
  setStorageData(data);
};

/**
 * Get the bearer token for the active account
 * Used for API requests when in multi-account mode
 * @returns {string | null} Bearer token or null
 */
export const getActiveAccountToken = () => {
  const account = getActiveAccount();
  return account?.accessToken || null;
};

/**
 * Remove accounts from localStorage that are not in the authenticated set.
 * Called on page load to prune stale accounts after session restarts.
 * @param {number[]} authenticatedUserIds - User IDs with valid server sessions
 */
export const pruneStaleAccounts = (authenticatedUserIds) => {
  const validIds = new Set(authenticatedUserIds);
  const storedAccounts = getAccounts();
  storedAccounts.forEach((account) => {
    if (!validIds.has(account.userId)) {
      removeAccount(account.userId);
    }
  });
};

/**
 * Check if multi-account feature is available
 * @returns {boolean} True if feature is enabled
 */
export const isMultiAccountEnabled = () => {
  return (
    typeof window !== "undefined" &&
    window._tkcEnabledFeatures?.includes("multi_account_switcher")
  );
};
